"use client";

import { useEffect, useState } from "react";
import type { UserTagSuggestion } from "@/components/posts/user-tag-picker";
import { Label } from "@/components/ui/label";
import {
  type Participant,
  suggestionToParticipant,
  TeamCard,
  ValidationWarning,
} from "./base-match-template";
import { ActivityCourtPreview, ActivityDisplayPreview } from "./previews";
import { PadelScoreEditor } from "./score-editors/padel-score-editor";

// ──────────────────────────────────────────────────────────────────────────────
// TYPES - Padel is always doubles
// ──────────────────────────────────────────────────────────────────────────────

export type PadelTemplateData = {
  matchScore: { yourSets: number; opponentSets: number };
  /** Optional per-set game scores (e.g. 6–4, 7–5). */
  sets?: Array<{ yourScore: number; opponentScore: number }>;
  positions: Record<string, "your-team" | "opponent-team">;
  hasScore?: boolean;
};

export type PadelParticipant = Participant;

// ──────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type PadelTemplateProps = {
  currentUser: PadelParticipant;
  onChange: (data: PadelTemplateData, participants: PadelParticipant[]) => void;
  initialData?: Partial<PadelTemplateData>;
};

export function PadelTemplate({
  currentUser,
  onChange,
  initialData,
}: PadelTemplateProps) {
  const [participants, setParticipants] = useState<PadelParticipant[]>([
    currentUser,
  ]);
  const [teams, setTeams] = useState<
    Record<string, "your-team" | "opponent-team">
  >(() => ({
    [currentUser.id]: "your-team",
    ...(initialData?.positions ?? {}),
  }));
  type PadelScorePart = Pick<PadelTemplateData, "matchScore" | "sets">;
  const [scorePart, setScorePart] = useState<PadelScorePart>(() => ({
    matchScore: initialData?.matchScore ?? { yourSets: 0, opponentSets: 0 },
    sets: initialData?.sets ?? [],
  }));

  // Padel is always doubles (4 players)
  const yourTeamMax = 2;
  const opponentTeamMax = 2;

  const yourTeam = participants.filter((p) => teams[p.id] === "your-team");
  const opponentTeam = participants.filter(
    (p) => teams[p.id] === "opponent-team",
  );
  const selectedUserIds = new Set(participants.map((p) => p.id));

  useEffect(() => {
    const data: PadelTemplateData = {
      matchScore: scorePart.matchScore,
      sets: scorePart.sets ?? [],
      positions: teams,
      hasScore: true,
    };
    onChange(data, participants);
  }, [scorePart, teams, participants, onChange]);

  const handleAddParticipant = (
    suggestion: UserTagSuggestion,
    team: "your-team" | "opponent-team",
  ) => {
    if (participants.length >= 4) return;
    const teamCount =
      team === "your-team" ? yourTeam.length : opponentTeam.length;
    const teamMax = team === "your-team" ? yourTeamMax : opponentTeamMax;
    if (teamCount >= teamMax) return;

    const newParticipant = suggestionToParticipant(suggestion);
    setParticipants((prev) => [...prev, newParticipant]);
    setTeams((prev) => ({ ...prev, [suggestion.id]: team }));
  };

  const handleRemoveParticipant = (userId: string) => {
    if (userId === currentUser.id) return;
    setParticipants((prev) => prev.filter((p) => p.id !== userId));
    setTeams((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const yourSets = scorePart.matchScore.yourSets;
  const opponentSets = scorePart.matchScore.opponentSets;
  const yourWon = yourSets > opponentSets;
  const opponentWon = opponentSets > yourSets;
  const isDraw = yourSets === opponentSets && yourSets > 0;
  const isYourTeamComplete = yourTeam.length === yourTeamMax;
  const isOpponentComplete = opponentTeam.length === opponentTeamMax;
  const isComplete = isYourTeamComplete && isOpponentComplete;

  return (
    <div className="space-y-6">
      {/* Note about doubles */}
      <div className="rounded-lg bg-brand/10 px-3 py-2 text-sm text-brand">
        🎾 Padel is played in doubles teams (2v2)
      </div>

      <div className="grid grid-cols-2 gap-3">
        <TeamCard
          label="Team 1"
          isYourTeam
          players={yourTeam}
          maxPlayers={yourTeamMax}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "your-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={yourWon || isDraw}
          isComplete={isYourTeamComplete}
        />
        <TeamCard
          label="Team 2"
          isYourTeam={false}
          players={opponentTeam}
          maxPlayers={opponentTeamMax}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "opponent-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={opponentWon}
          isComplete={isOpponentComplete}
        />
      </div>

      {!isComplete && (
        <ValidationWarning
          message={
            !isYourTeamComplete && !isOpponentComplete
              ? "Add players to both teams (2 per team)"
              : !isYourTeamComplete
                ? `Add ${yourTeamMax - yourTeam.length} more teammate${yourTeamMax - yourTeam.length > 1 ? "s" : ""}`
                : `Add ${opponentTeamMax - opponentTeam.length} more opponent${opponentTeamMax - opponentTeam.length > 1 ? "s" : ""}`
          }
        />
      )}

      <PadelScoreEditor
        initialData={initialData as unknown as Record<string, unknown>}
        onChange={(data) => setScorePart(data as unknown as PadelScorePart)}
      />

      {participants.length >= 2 && (
        <div className="space-y-2">
          <Label className="text-text-primary">Preview</Label>
          <ActivityCourtPreview
            activityId="padel"
            yourTeam={yourTeam}
            opponentTeam={opponentTeam}
            yourWon={yourWon}
          />
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// DISPLAY COMPONENT (for feed)
// ──────────────────────────────────────────────────────────────────────────────

type PadelDisplayProps = {
  data: PadelTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

export function PadelDisplay({ data, participants }: PadelDisplayProps) {
  return (
    <ActivityDisplayPreview
      activityId="padel"
      data={data}
      participants={participants}
    />
  );
}
